import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import TwoFactorDialog from "@/components/TwoFactorDialog";
import { 
  PawPrint, ChevronRight, ArrowRight, Shield, Heart, Sparkles,
  Syringe, FileText, MapPin, Users, Phone, Search, ShoppingBag,
  Lightbulb, Share2, QrCode, Bell, Camera, Check, Star, Play, Mail,
  Eye, EyeOff, CheckCircle, Calendar, Stethoscope, Building2, Dog, Cat,
  Bird, Rabbit, Clock, Activity, Home, Lock, Plus, Globe, Smartphone
} from "lucide-react";

// Image URLs
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1623387641168-d9803ddd3f35?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvZyUyMGNhdCUyMHRvZ2V0aGVyJTIwcGV0fGVufDB8fHx8MTc3OTQ3NDczOXww&ixlib=rb-4.1.0&q=85",
  dogCat: "https://images.unsplash.com/photo-1450778869180-41d0601e046e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGRvZyUyMGNhdCUyMHRvZ2V0aGVyJTIwcGV0fGVufDB8fHx8MTc3OTQ3NDczOXww&ixlib=rb-4.1.0&q=85",
  horse: "https://images.unsplash.com/photo-1598974357801-cbca100e65d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxob3JzZSUyMHBvcnRyYWl0JTIwYmVhdXRpZnVsfGVufDB8fHx8MTc3OTQ3NDc0MXww&ixlib=rb-4.1.0&q=85",
  vet: "https://images.unsplash.com/photo-1770836037793-95bdbf190f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHx2ZXRlcmluYXJpYW4lMjB3aXRoJTIwcGV0JTIwY2xpbmljfGVufDB8fHx8MTc3OTQ3NDc0MXww&ixlib=rb-4.1.0&q=85",
  shelter: "https://images.unsplash.com/photo-1594004844563-536a03a6e532?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxhbmltYWwlMjBzaGVsdGVyJTIwZG9nJTIwYWRvcHRpb258ZW58MHx8fHwxNzc5NDc0NzQzfDA&ixlib=rb-4.1.0&q=85",
  bengalCat: "https://images.pexels.com/photos/16395150/pexels-photo-16395150.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
};

export default function Landing() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [userType, setUserType] = useState(null); // 'cliente', 'vet', 'canile'
  
  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [pending2FAData, setPending2FAData] = useState(null);
  const [twoFAMode, setTwoFAMode] = useState("verify");
  
  // Form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [pendingRegistration, setPendingRegistration] = useState(null);
  const [showTerms, setShowTerms] = useState(false);

  // Reset userType when dialog closes
  const handleAuthClose = (open) => {
    setShowAuth(open);
    if (!open) setUserType(null);
  };

  useEffect(() => {
    setMounted(true);
    // Force light mode on landing page
    document.documentElement.classList.remove('dark');
    return () => {
      // Restore system preference when leaving
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark');
      }
    };
  }, []);

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    window.location.href = authUrl;
  };

  const handleAppleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&provider=apple`;
    window.location.href = authUrl;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.requires_2fa) {
          setPending2FAData({ ...data, email: loginEmail, password: loginPassword });
          setTwoFAMode("verify");
          setShow2FA(true);
        } else {
          if (data.session_token) {
            localStorage.setItem('session_token', data.session_token);
          }
          login(data);
          toast.success("Benvenuto!");
          navigate('/dashboard');
        }
      } else {
        if (data.detail === "account_exists_google") {
          toast.error("Hai già un account Google. Usa 'Continua con Google'.");
        } else if (data.detail === "account_exists_apple") {
          toast.error("Hai già un account Apple. Usa 'Continua con Apple'.");
        } else {
          toast.error(data.detail || "Credenziali non valide");
        }
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setPendingRegistration({ name: registerName, email: registerEmail, password: registerPassword });
    setShowTerms(true);
  };

  const confirmRegistration = async () => {
    if (!pendingRegistration) return;
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pendingRegistration),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.session_token) {
          localStorage.setItem('session_token', data.session_token);
        }
        login(data);
        toast.success("Account creato!");
        navigate('/dashboard');
      } else {
        toast.error(data.detail || "Errore nella registrazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setIsSubmitting(false);
      setShowTerms(false);
      setPendingRegistration(null);
    }
  };

  const handle2FAVerify = async (code) => {
    if (!pending2FAData) return;
    
    try {
      const response = await fetch(`${API}/auth/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: pending2FAData.email,
          password: pending2FAData.password,
          totp_code: code
        }),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.session_token) {
          localStorage.setItem('session_token', data.session_token);
        }
        login(data);
        setShow2FA(false);
        toast.success("Benvenuto!");
        navigate('/dashboard');
      } else {
        toast.error(data.detail || "Codice non valido");
      }
    } catch (error) {
      toast.error("Errore di verifica");
    }
  };

  if (!mounted) return null;

  return (
    <div className="min-h-screen bg-[#F5F5F7] overflow-x-hidden">
      {/* Header - Glassmorphism */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center shadow-lg shadow-[#FF6B4A]/20">
                <PawPrint className="text-white" size={18} />
              </div>
              <span className="font-bold text-[#1D1D1F] text-lg tracking-tight">Zampa</span>
            </div>
            
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-[#86868B] hover:text-[#1D1D1F] transition-colors">Funzionalità</a>
              <a href="#veterinari" className="text-sm text-[#86868B] hover:text-[#1D1D1F] transition-colors">Per Veterinari</a>
              <a href="#associazioni" className="text-sm text-[#86868B] hover:text-[#1D1D1F] transition-colors">Associazioni</a>
            </nav>
            
            <Button 
              onClick={() => setShowAuth(true)}
              className="bg-[#1D1D1F] hover:bg-[#1D1D1F]/90 text-white rounded-full px-5 h-9 text-sm font-medium"
              data-testid="header-login-btn"
            >
              Registrati o Accedi
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - MOBILE vs DESKTOP */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-8 sm:pb-12 lg:pb-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          
          {/* ============ MOBILE ONLY - Apple Storytelling Style ============ */}
          <div className="lg:hidden">
            {/* Hero - Big Statement */}
            <div className="text-center mb-10">
              <h1 className="text-[2.5rem] font-bold text-[#1D1D1F] tracking-tight leading-[1.1] mb-5" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif' }}>
                Ogni momento conta.
              </h1>
              <p className="text-lg text-[#86868B] leading-relaxed max-w-xs mx-auto">
                Il primo fascicolo digitale certificato per chi ama i propri animali.
              </p>
            </div>
            
            {/* Story 1: Emotional Photo + Statement */}
            <div className="mb-10">
              <div className="relative rounded-3xl overflow-hidden mb-6">
                <img 
                  src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800&q=80" 
                  alt="Cane felice" 
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-white/80 text-sm mb-1">Fascicolo Digitale</p>
                  <h2 className="text-white text-2xl font-bold leading-tight">
                    Tutto quello che ami,<br />sempre con te.
                  </h2>
                </div>
              </div>
              <p className="text-[#86868B] text-base leading-relaxed px-1">
                Vaccini, documenti, visite veterinarie. Un unico posto sicuro per tutti i ricordi e le informazioni importanti del tuo compagno.
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="flex justify-center mb-12">
              <Button 
                onClick={() => setShowAuth(true)} 
                className="h-12 px-8 bg-[#FF6B4A] hover:bg-[#E55A3C] text-white rounded-full text-base font-semibold transition-transform hover:scale-105" 
                data-testid="hero-cta"
              >
                Inizia gratis
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
            
            {/* Story 2: App Preview - Clean Device */}
            <div className="mb-12">
              <div className="text-center mb-6">
                <p className="text-[#FF6B4A] text-sm font-medium mb-2">L'app Zampa</p>
                <h2 className="text-2xl font-bold text-[#1D1D1F] leading-tight">
                  Progettata per<br />semplificare la vita.
                </h2>
              </div>
              
              {/* iPhone Mockup - Centered */}
              <div className="flex justify-center">
                <div className="relative w-[200px]">
                  <div className="bg-[#1D1D1F] rounded-[2.5rem] p-[8px] shadow-2xl">
                    <div className="bg-white rounded-[2rem] overflow-hidden" style={{ aspectRatio: '9/19.5' }}>
                      <div className="flex justify-center pt-2.5 pb-1.5">
                        <div className="w-20 h-6 bg-[#1D1D1F] rounded-full" />
                      </div>
                      <div className="px-3 pb-3">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-7 h-7 rounded-lg bg-[#FF6B4A] flex items-center justify-center">
                              <PawPrint className="text-white" size={14} />
                            </div>
                            <span className="font-semibold text-xs text-[#1D1D1F]">Zampa</span>
                          </div>
                          <Bell className="text-[#86868B]" size={16} />
                        </div>
                        <div className="bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] rounded-xl p-3 text-white mb-2">
                          <div className="flex gap-2.5">
                            <div className="w-14 h-14 rounded-lg bg-white/20 overflow-hidden flex-shrink-0">
                              <img src={IMAGES.hero} alt="Luna" className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <p className="text-[9px] text-white/70 uppercase">Il tuo pet</p>
                              <p className="font-bold text-base">Luna</p>
                              <p className="text-[10px] text-white/90">Golden Retriever</p>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          <div className="bg-[#F5F5F7] rounded-lg p-2">
                            <Syringe className="text-[#2E7D32] mb-0.5" size={14} />
                            <p className="text-[9px] text-[#86868B]">Vaccino</p>
                            <p className="font-semibold text-xs text-[#1D1D1F]">15 Dic</p>
                          </div>
                          <div className="bg-[#F5F5F7] rounded-lg p-2">
                            <Bell className="text-[#FF6B4A] mb-0.5" size={14} />
                            <p className="text-[9px] text-[#86868B]">Promemoria</p>
                            <p className="font-semibold text-xs text-[#1D1D1F]">3 attivi</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-center pb-1.5 mt-auto">
                        <div className="w-24 h-1 bg-[#1D1D1F] rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Story 3: Vaccini - Lifestyle Photo */}
            <div className="mb-10">
              <div className="relative rounded-3xl overflow-hidden mb-5">
                <img 
                  src="https://images.unsplash.com/photo-1548199973-03cce0bbc87b?w=800&q=80" 
                  alt="Cani che corrono" 
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                      <Syringe className="text-[#2E7D32]" size={16} />
                    </div>
                    <span className="text-white/80 text-sm">Vaccini</span>
                  </div>
                  <h2 className="text-white text-xl font-bold leading-tight">
                    Mai più una scadenza dimenticata.
                  </h2>
                </div>
              </div>
              <p className="text-[#86868B] text-sm leading-relaxed px-1">
                Ricevi notifiche automatiche per vaccini, richiami e visite di controllo. La salute del tuo amico, sempre sotto controllo.
              </p>
            </div>
            
            {/* Story 4: Promemoria - Another Lifestyle */}
            <div className="mb-10">
              <div className="relative rounded-3xl overflow-hidden mb-5">
                <img 
                  src="https://images.unsplash.com/photo-1495360010541-f48722b34f7d?w=800&q=80" 
                  alt="Gatto rilassato" 
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg bg-[#FFF3E0] flex items-center justify-center">
                      <Bell className="text-[#FF6B4A]" size={16} />
                    </div>
                    <span className="text-white/80 text-sm">Promemoria</span>
                  </div>
                  <h2 className="text-white text-xl font-bold leading-tight">
                    Frontline, vermifugo,<br />toelettatura. Fatto.
                  </h2>
                </div>
              </div>
              <p className="text-[#86868B] text-sm leading-relaxed px-1">
                Imposta promemoria ricorrenti per antiparassitari, medicine e appuntamenti. Zampa ti ricorda tutto.
              </p>
            </div>
            
            {/* Story 5: QR Code Identity */}
            <div className="mb-12">
              <div className="bg-[#F5F5F7] rounded-3xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                    <QrCode className="text-[#1D1D1F]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1D1D1F]">Carta d'identità digitale</h3>
                    <p className="text-sm text-[#86868B]">QR Code univoco</p>
                  </div>
                </div>
                <p className="text-[#86868B] text-sm leading-relaxed">
                  Condividi le informazioni del tuo pet con veterinari, dog sitter o chiunque ne abbia bisogno. Un QR code, tutte le info.
                </p>
              </div>
            </div>
            
            {/* Multi-species - Elegant */}
            <div className="text-center mb-10">
              <p className="text-xs text-[#86868B] uppercase tracking-wider mb-4">Per tutti i tuoi compagni</p>
              <div className="flex justify-center gap-8">
                {[
                  { icon: Dog, label: "Cani" },
                  { icon: Cat, label: "Gatti" },
                  { icon: Bird, label: "Uccelli" },
                  { icon: Rabbit, label: "Conigli" }
                ].map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-1.5 text-[#86868B]">
                    <item.icon size={28} strokeWidth={1.5} />
                    <span className="text-xs">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Final CTA */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#1D1D1F] mb-4">
                Inizia oggi. Gratis.
              </h2>
              <Button 
                onClick={() => setShowAuth(true)} 
                className="h-12 px-8 bg-[#FF6B4A] hover:bg-[#E55A3C] text-white rounded-full text-base font-semibold" 
                data-testid="hero-cta-bottom"
              >
                Crea il tuo account
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
            
            {/* Trust - Minimal */}
            <div className="flex justify-center gap-6 text-[#86868B]">
              <div className="flex items-center gap-1.5">
                <Shield size={14} className="text-[#2E7D32]" />
                <span className="text-xs">Certificato</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Lock size={14} />
                <span className="text-xs">Sicuro</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Heart size={14} className="text-[#E91E63]" />
                <span className="text-xs">Gratuito</span>
              </div>
            </div>
          </div>
          
          {/* ============ DESKTOP ONLY - 2 columns with iMac ============ */}
          <div className="hidden lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md border border-black/5 mb-6">
                  <Sparkles className="text-[#FF6B4A]" size={18} />
                  <span className="text-sm font-medium text-[#1D1D1F]">Il tuo co-pilot per ogni animale</span>
                </div>
                <h1 className="text-6xl xl:text-7xl font-black text-[#1D1D1F] tracking-tight leading-[1.05] mb-6">
                  Ogni momento.<br /><span className="text-[#FF6B4A]">Ogni animale.</span>
                </h1>
                <p className="text-xl text-[#86868B] max-w-lg leading-relaxed mb-8">
                  Il fascicolo digitale certificato per i tuoi animali. Vaccini, documenti, promemoria, veterinari e molto altro in un'unica app.
                </p>
              </div>
              <div className="flex items-center gap-4">
                <Button onClick={() => setShowAuth(true)} className="h-14 px-10 bg-[#FF6B4A] hover:bg-[#E55A3C] text-white rounded-full text-lg font-semibold shadow-xl shadow-[#FF6B4A]/30 transition-all hover:-translate-y-1" data-testid="hero-cta-desktop">
                  Inizia gratis<ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="h-14 px-8 rounded-full text-lg font-medium border-2">
                  Scopri di più
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-[#E8F5E9] flex items-center justify-center"><Shield className="text-[#2E7D32]" size={20} /></div>
                  <div><p className="font-semibold text-sm text-[#1D1D1F]">Certificato</p><p className="text-xs text-[#86868B]">Dati protetti</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-[#E3F2FD] flex items-center justify-center"><Lock className="text-blue-600" size={20} /></div>
                  <div><p className="font-semibold text-sm text-[#1D1D1F]">Sicuro</p><p className="text-xs text-[#86868B]">Crittografia</p></div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl bg-[#FCE4EC] flex items-center justify-center"><Heart className="text-[#E91E63]" size={20} /></div>
                  <div><p className="font-semibold text-sm text-[#1D1D1F]">Gratuito</p><p className="text-xs text-[#86868B]">Per sempre</p></div>
                </div>
              </div>
            </div>
            
            {/* iMac 24" Style Mockup - Premium Apple Design */}
            <div className="relative flex justify-center">
              {/* iMac 24" mockup */}
              <div className="relative w-full max-w-[580px]">
                {/* Screen with silver aluminum bezels */}
                <div className="bg-gradient-to-b from-[#E8E8ED] to-[#D8D8DC] rounded-[28px] p-[12px] shadow-2xl">
                  {/* Inner black bezel */}
                  <div className="bg-[#1D1D1F] rounded-[20px] p-[3px]">
                    {/* Screen content */}
                    <div className="bg-white rounded-[18px] overflow-hidden" style={{ aspectRatio: '16/10' }}>
                      {/* macOS style top bar */}
                      <div className="bg-gradient-to-b from-[#F5F5F7] to-[#EBEBF0] px-4 py-2 flex items-center border-b border-black/5">
                        <div className="flex gap-2">
                          <div className="w-3 h-3 rounded-full bg-[#FF5F57] shadow-inner" />
                          <div className="w-3 h-3 rounded-full bg-[#FFBD2E] shadow-inner" />
                          <div className="w-3 h-3 rounded-full bg-[#28CA41] shadow-inner" />
                        </div>
                        <div className="flex-1 flex justify-center">
                          <div className="bg-white/80 backdrop-blur rounded-lg px-4 py-1 text-sm text-[#86868B] flex items-center gap-2 shadow-sm border border-black/5">
                            <Lock size={11} className="text-[#2E7D32]" />
                            <span className="text-[#1D1D1F]">zampa.one</span>
                          </div>
                        </div>
                        <div className="w-16" />
                      </div>
                      
                      {/* Zampa Web App Dashboard - Light theme */}
                      <div className="bg-[#FAFAFA] p-4 h-full">
                        {/* App header */}
                        <div className="bg-white rounded-2xl p-3 mb-3 shadow-sm border border-black/5 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center shadow-lg shadow-[#FF6B4A]/20">
                              <PawPrint className="text-white" size={20} />
                            </div>
                            <div>
                              <span className="font-bold text-base text-[#1D1D1F]">Zampa</span>
                              <p className="text-[10px] text-[#86868B]">Dashboard</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <Bell className="text-[#86868B]" size={18} />
                              <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#FF6B4A] rounded-full" />
                            </div>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center text-white text-sm font-bold shadow-sm">M</div>
                          </div>
                        </div>
                        
                        {/* Dashboard bento grid */}
                        <div className="grid grid-cols-12 gap-3">
                          {/* Main pet card - larger */}
                          <div className="col-span-5 bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] rounded-2xl p-3 text-white shadow-lg shadow-[#FF6B4A]/20">
                            <div className="w-full h-16 rounded-xl bg-white/20 overflow-hidden mb-2">
                              <img src={IMAGES.hero} alt="Luna" className="w-full h-full object-cover" />
                            </div>
                            <p className="font-bold text-sm">Luna</p>
                            <p className="text-[10px] text-white/80">Golden Retriever</p>
                            <div className="flex gap-1 mt-1.5">
                              <span className="bg-white/25 px-1.5 py-0.5 rounded text-[8px] font-medium">Vaccini OK</span>
                              <span className="bg-white/25 px-1.5 py-0.5 rounded text-[8px] font-medium">Assicurato</span>
                            </div>
                          </div>
                          
                          {/* Stats column */}
                          <div className="col-span-4 space-y-2">
                            <div className="bg-white rounded-xl p-2.5 shadow-sm border border-black/5">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <div className="w-5 h-5 rounded-md bg-[#E8F5E9] flex items-center justify-center">
                                  <Syringe className="text-[#2E7D32]" size={12} />
                                </div>
                                <span className="text-[10px] font-medium text-[#86868B]">Prossimo vaccino</span>
                              </div>
                              <p className="text-base font-bold text-[#1D1D1F]">15 Dicembre</p>
                            </div>
                            <div className="bg-white rounded-xl p-2.5 shadow-sm border border-black/5">
                              <div className="flex items-center gap-1.5 mb-0.5">
                                <div className="w-5 h-5 rounded-md bg-[#FFF3E0] flex items-center justify-center">
                                  <Bell className="text-[#FF6B4A]" size={12} />
                                </div>
                                <span className="text-[10px] font-medium text-[#86868B]">Promemoria attivi</span>
                              </div>
                              <p className="text-base font-bold text-[#1D1D1F]">3</p>
                            </div>
                          </div>
                          
                          {/* Quick actions */}
                          <div className="col-span-3 space-y-2">
                            <div className="bg-white rounded-xl p-2 shadow-sm border border-black/5 flex items-center gap-2 hover:bg-[#F5F5F7] transition-colors cursor-pointer">
                              <div className="w-7 h-7 rounded-lg bg-purple-50 flex items-center justify-center">
                                <QrCode className="text-purple-600" size={14} />
                              </div>
                              <span className="text-[10px] font-medium text-[#1D1D1F]">QR Code</span>
                            </div>
                            <div className="bg-white rounded-xl p-2 shadow-sm border border-black/5 flex items-center gap-2 hover:bg-[#F5F5F7] transition-colors cursor-pointer">
                              <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center">
                                <FileText className="text-blue-600" size={14} />
                              </div>
                              <span className="text-[10px] font-medium text-[#1D1D1F]">Documenti</span>
                            </div>
                            <div className="bg-white rounded-xl p-2 shadow-sm border-2 border-dashed border-[#E5E5EA] flex items-center gap-2 hover:border-[#FF6B4A] transition-colors cursor-pointer">
                              <div className="w-7 h-7 rounded-lg bg-[#FFF3E0] flex items-center justify-center">
                                <Plus className="text-[#FF6B4A]" size={14} />
                              </div>
                              <span className="text-[10px] font-medium text-[#86868B]">Aggiungi</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* iMac Stand - more realistic */}
                <div className="flex justify-center">
                  <div className="w-28 h-20 bg-gradient-to-b from-[#D8D8DC] to-[#C8C8CC]" style={{ clipPath: 'polygon(20% 0%, 80% 0%, 95% 100%, 5% 100%)' }} />
                </div>
                <div className="flex justify-center -mt-0.5">
                  <div className="w-44 h-2.5 bg-gradient-to-b from-[#C8C8CC] to-[#B8B8BC] rounded-full shadow-md" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Notification badges - UNDER hero, readable */}
      <section className="hidden lg:block py-8 bg-white border-b border-black/5">
        <div className="max-w-5xl mx-auto px-8">
          <div className="grid grid-cols-4 gap-6">
            <div className="flex items-center gap-3 bg-[#F5F5F7] rounded-2xl p-4">
              <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center"><Syringe className="text-[#2E7D32]" size={24} /></div>
              <div><p className="font-bold text-sm text-[#1D1D1F]">Vaccini tracciati</p><p className="text-xs text-[#86868B]">Avvisi automatici</p></div>
            </div>
            <div className="flex items-center gap-3 bg-[#F5F5F7] rounded-2xl p-4">
              <div className="w-12 h-12 rounded-xl bg-[#FFF3E0] flex items-center justify-center"><Bell className="text-[#FF6B4A]" size={24} /></div>
              <div><p className="font-bold text-sm text-[#1D1D1F]">Promemoria smart</p><p className="text-xs text-[#86868B]">Frontline, vermifugo</p></div>
            </div>
            <div className="flex items-center gap-3 bg-[#F5F5F7] rounded-2xl p-4">
              <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center"><QrCode className="text-purple-600" size={24} /></div>
              <div><p className="font-bold text-sm text-[#1D1D1F]">QR Code ID</p><p className="text-xs text-[#86868B]">Condividi ovunque</p></div>
            </div>
            <div className="flex items-center gap-3 bg-[#F5F5F7] rounded-2xl p-4">
              <div className="w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center"><Calendar className="text-pink-600" size={24} /></div>
              <div><p className="font-bold text-sm text-[#1D1D1F]">Calendario visite</p><p className="text-xs text-[#86868B]">Appuntamenti vet</p></div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Multi-species banner - Desktop only */}
      <section className="hidden lg:block py-8 bg-white border-y border-black/5">
        <div className="max-w-7xl mx-auto px-8">
          <p className="text-center text-sm text-[#86868B] mb-6">Per tutti i tuoi amici</p>
          <div className="flex justify-center items-center gap-16">
            {[
              { icon: Dog, label: "Cani" },
              { icon: Cat, label: "Gatti" },
              { icon: () => (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 9s-2-2-4-2c-1.7 0-3 1-4 2l-3 3c-1 1-2.3 2-4 2-2 0-4-2-4-2"/>
                  <path d="M4 19h5l3-3 4 4h4v-4l-4-4"/>
                  <path d="M18 4l-4 4"/>
                  <path d="M2 11l2 2"/>
                </svg>
              ), label: "Cavalli" },
              { icon: Bird, label: "Uccelli" },
              { icon: Rabbit, label: "Conigli" }
            ].map((item, i) => (
              <div key={i} className="flex flex-col items-center gap-2 text-[#86868B] hover:text-[#1D1D1F] transition-colors cursor-pointer">
                <item.icon size={32} /><span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FUNZIONALITÀ PRINCIPALI - Mobile: 1 col, Desktop: detailed storytelling */}
      <section id="features" className="py-6 sm:py-10 lg:py-20 px-4 sm:px-6 lg:px-8 bg-[#F5F5F7]">
        <div className="max-w-7xl mx-auto">
          
          {/* Desktop title */}
          <div className="hidden lg:block text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1D1D1F] mb-4">Tutto quello che ti serve</h2>
            <p className="text-xl text-[#86868B] max-w-2xl mx-auto">Un ecosistema completo per prenderti cura dei tuoi animali. Scopri ogni funzionalità nel dettaglio.</p>
          </div>
          
          {/* Mobile: stacked cards */}
          <div className="lg:hidden space-y-4">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5">
              <div className="aspect-[2/1] relative overflow-hidden">
                <img src={IMAGES.hero} alt="Documenti pet" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
                      <FileText className="text-white" size={16} />
                    </div>
                    <h3 className="font-bold text-white text-base">Fascicolo Digitale</h3>
                  </div>
                  <p className="text-white/90 text-xs">Passaporto, libretto sanitario, certificati.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5">
              <div className="aspect-[2/1] relative overflow-hidden">
                <img src={IMAGES.vet} alt="Veterinario" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                      <Syringe className="text-[#2E7D32]" size={16} />
                    </div>
                    <h3 className="font-bold text-white text-base">Vaccini</h3>
                  </div>
                  <p className="text-white/90 text-xs">Traccia scadenze e avvisi automatici.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5">
              <div className="aspect-[2/1] relative overflow-hidden">
                <img src={IMAGES.bengalCat} alt="Gatto" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-[#FFF3E0] flex items-center justify-center">
                      <Bell className="text-[#FF6B4A]" size={16} />
                    </div>
                    <h3 className="font-bold text-white text-base">Promemoria</h3>
                  </div>
                  <p className="text-white/90 text-xs">Frontline, vermifugo, toelettatura.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5">
              <div className="aspect-[2/1] relative overflow-hidden">
                <img src={IMAGES.horse} alt="Cavallo" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                      <QrCode className="text-purple-600" size={16} />
                    </div>
                    <h3 className="font-bold text-white text-base">QR Code Identità</h3>
                  </div>
                  <p className="text-white/90 text-xs">Carta d'identità digitale con QR.</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* DESKTOP: Detailed storytelling sections */}
          <div className="hidden lg:block space-y-24">
            
            {/* Feature 1: Fascicolo Digitale */}
            <div className="grid grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FF6B4A] to-[#FF8F6B] rounded-full">
                  <FileText className="text-white" size={18} />
                  <span className="text-sm font-semibold text-white">Fascicolo Digitale</span>
                </div>
                <h3 className="text-4xl font-bold text-[#1D1D1F] leading-tight">
                  Tutti i documenti del tuo pet in un unico posto
                </h3>
                <p className="text-lg text-[#86868B] leading-relaxed">
                  Carica passaporto, libretto sanitario, certificati e qualsiasi documento importante. Il nostro OCR intelligente estrae automaticamente dati come vaccini, microchip e date di scadenza.
                </p>
                <ul className="space-y-3">
                  {["Scansione OCR automatica", "Riconoscimento passaporto UE", "Organizzazione per categoria", "Backup sicuro nel cloud"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="text-[#2E7D32]" size={20} />
                      <span className="text-[#1D1D1F]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-xl border border-black/5">
                {/* App screenshot mockup - Fascicolo */}
                <div className="bg-[#F5F5F7] rounded-2xl p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
                      <FileText className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="font-bold text-[#1D1D1F]">Documenti di Luna</p>
                      <p className="text-xs text-[#86868B]">3 documenti caricati</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: "Passaporto UE", date: "Caricato il 15/03/2026", icon: FileText, color: "bg-blue-50 text-blue-600" },
                      { name: "Libretto Sanitario", date: "Caricato il 10/01/2026", icon: FileText, color: "bg-green-50 text-green-600" },
                      { name: "Certificato Microchip", date: "Caricato il 05/03/2022", icon: FileText, color: "bg-purple-50 text-purple-600" }
                    ].map((doc, i) => (
                      <div key={i} className="bg-white rounded-xl p-3 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${doc.color.split(' ')[0]} flex items-center justify-center`}>
                          <doc.icon className={doc.color.split(' ')[1]} size={18} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-sm text-[#1D1D1F]">{doc.name}</p>
                          <p className="text-xs text-[#86868B]">{doc.date}</p>
                        </div>
                        <ChevronRight className="text-[#86868B]" size={16} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Feature 2: Calendario e Promemoria */}
            <div className="grid grid-cols-2 gap-16 items-center">
              <div className="bg-white rounded-3xl p-6 shadow-xl border border-black/5 order-1">
                {/* App screenshot mockup - Calendario */}
                <div className="bg-[#F5F5F7] rounded-2xl p-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center">
                        <Calendar className="text-pink-600" size={20} />
                      </div>
                      <div>
                        <p className="font-bold text-[#1D1D1F]">Maggio 2026</p>
                        <p className="text-xs text-[#86868B]">3 appuntamenti</p>
                      </div>
                    </div>
                  </div>
                  {/* Mini calendar */}
                  <div className="bg-white rounded-xl p-3 mb-3">
                    <div className="grid grid-cols-7 gap-1 text-center text-xs mb-2">
                      {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => (
                        <span key={i} className="text-[#86868B] font-medium">{d}</span>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center text-xs">
                      {[...Array(31)].map((_, i) => (
                        <span key={i} className={`py-1 rounded ${i === 14 ? 'bg-[#FF6B4A] text-white font-bold' : i === 21 ? 'bg-[#E8F5E9] text-[#2E7D32] font-bold' : i === 27 ? 'bg-[#FFF3E0] text-[#FF6B4A] font-bold' : 'text-[#1D1D1F]'}`}>
                          {i + 1}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-[#FF6B4A]/10 rounded-lg p-2 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#FF6B4A]" />
                      <span className="text-xs font-medium text-[#1D1D1F]">15 Mag - Visita veterinaria</span>
                    </div>
                    <div className="bg-[#E8F5E9] rounded-lg p-2 flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-[#2E7D32]" />
                      <span className="text-xs font-medium text-[#1D1D1F]">22 Mag - Vaccino richiamo</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-6 order-2">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full">
                  <Calendar className="text-pink-600" size={18} />
                  <span className="text-sm font-semibold text-pink-600">Calendario & Promemoria</span>
                </div>
                <h3 className="text-4xl font-bold text-[#1D1D1F] leading-tight">
                  Mai più una scadenza dimenticata
                </h3>
                <p className="text-lg text-[#86868B] leading-relaxed">
                  Gestisci appuntamenti veterinari, promemoria per Frontline, antiparassitari, vermifugo e toelettatura. Ricevi notifiche automatiche prima di ogni scadenza importante.
                </p>
                <ul className="space-y-3">
                  {["Calendario visuale integrato", "Promemoria ricorrenti automatici", "Notifiche personalizzabili", "Sincronizzazione multi-dispositivo"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="text-[#2E7D32]" size={20} />
                      <span className="text-[#1D1D1F]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            {/* Feature 3: QR Code e Condivisione */}
            <div className="grid grid-cols-2 gap-16 items-center">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 rounded-full">
                  <QrCode className="text-purple-600" size={18} />
                  <span className="text-sm font-semibold text-purple-600">Carta d'Identità QR</span>
                </div>
                <h3 className="text-4xl font-bold text-[#1D1D1F] leading-tight">
                  Condividi le info del tuo pet istantaneamente
                </h3>
                <p className="text-lg text-[#86868B] leading-relaxed">
                  Genera una carta d'identità digitale completa con QR Code. Condividila con veterinari, dog sitter, pensioni o quando viaggi. Tutti i dati importanti accessibili in un secondo.
                </p>
                <ul className="space-y-3">
                  {["QR Code univoco per ogni pet", "Condivisione con link sicuro", "Esporta PDF ufficiale", "Perfetto per viaggi e emergenze"].map((item, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <CheckCircle className="text-[#2E7D32]" size={20} />
                      <span className="text-[#1D1D1F]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white rounded-3xl p-6 shadow-xl border border-black/5">
                {/* Pet ID Card mockup */}
                <div className="bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] rounded-2xl p-5 text-white">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-20 h-20 rounded-xl bg-white/20 overflow-hidden">
                      <img src={IMAGES.hero} alt="Luna" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-white/70 uppercase tracking-wider mb-1">Carta d'identità digitale</p>
                      <p className="text-2xl font-bold">Luna</p>
                      <p className="text-sm text-white/90">Golden Retriever • Femmina</p>
                    </div>
                    <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center">
                      <QrCode className="text-[#FF6B4A]" size={40} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
                    <div>
                      <p className="text-xs text-white/60">Microchip</p>
                      <p className="font-mono text-sm">380260012345678</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60">Data nascita</p>
                      <p className="text-sm">15/03/2022</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60">Vaccini</p>
                      <p className="text-sm flex items-center gap-1"><CheckCircle size={14} /> In regola</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60">Proprietario</p>
                      <p className="text-sm">Marco Rossi</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Altre funzionalità - Grid - Mobile: 2 col, Desktop: 3 col */}
      <section className="py-6 sm:py-10 lg:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-lg sm:text-xl lg:text-3xl font-bold text-[#1D1D1F] text-center mb-4 lg:mb-10">E molto altro...</h2>
          
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2.5 lg:gap-6">
            <div className="bg-[#F5F5F7] rounded-xl lg:rounded-2xl p-3 lg:p-6 flex items-center gap-2.5 lg:gap-4">
              <div className="w-9 h-9 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-pink-50 flex items-center justify-center flex-shrink-0">
                <Calendar className="text-pink-600" size={18} />
              </div>
              <div>
                <h3 className="font-bold text-xs lg:text-base text-[#1D1D1F]">Calendario</h3>
                <p className="text-[10px] lg:text-sm text-[#86868B]">Appuntamenti vet</p>
              </div>
            </div>
            
            <div className="bg-[#F5F5F7] rounded-xl lg:rounded-2xl p-3 lg:p-6 flex items-center gap-2.5 lg:gap-4">
              <div className="w-9 h-9 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                <MapPin className="text-blue-600" size={18} />
              </div>
              <div>
                <h3 className="font-bold text-xs lg:text-base text-[#1D1D1F]">Mappa Servizi</h3>
                <p className="text-[10px] lg:text-sm text-[#86868B]">Parchi e vet vicini</p>
              </div>
            </div>
            
            <div className="bg-[#F5F5F7] rounded-xl lg:rounded-2xl p-3 lg:p-6 flex items-center gap-2.5 lg:gap-4">
              <div className="w-9 h-9 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                <Users className="text-amber-600" size={18} />
              </div>
              <div>
                <h3 className="font-bold text-xs lg:text-base text-[#1D1D1F]">Dog Sitter</h3>
                <p className="text-[10px] lg:text-sm text-[#86868B]">Sitter verificati</p>
              </div>
            </div>
            
            <div className="bg-[#F5F5F7] rounded-xl lg:rounded-2xl p-3 lg:p-6 flex items-center gap-2.5 lg:gap-4">
              <div className="w-9 h-9 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-cyan-50 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="text-cyan-600" size={18} />
              </div>
              <div>
                <h3 className="font-bold text-xs lg:text-base text-[#1D1D1F]">Servizi</h3>
                <p className="text-[10px] lg:text-sm text-[#86868B]">Toelettatura, pensioni</p>
              </div>
            </div>
            
            <div className="bg-[#F5F5F7] rounded-xl lg:rounded-2xl p-3 lg:p-6 flex items-center gap-2.5 lg:gap-4">
              <div className="w-9 h-9 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Activity className="text-gray-700" size={18} />
              </div>
              <div>
                <h3 className="font-bold text-xs lg:text-base text-[#1D1D1F]">Storico Medico</h3>
                <p className="text-[10px] lg:text-sm text-[#86868B]">Visite e terapie</p>
              </div>
            </div>
            
            <div className="bg-[#F5F5F7] rounded-xl lg:rounded-2xl p-3 lg:p-6 flex items-center gap-2.5 lg:gap-4">
              <div className="w-9 h-9 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                <Share2 className="text-rose-600" size={18} />
              </div>
              <div>
                <h3 className="font-bold text-xs lg:text-base text-[#1D1D1F]">Condividi PDF</h3>
                <p className="text-[10px] lg:text-sm text-[#86868B]">Fascicolo ufficiale</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Social Proof */}
      <section className="py-5 sm:py-8 px-4 bg-[#F5F5F7]">
        <div className="max-w-3xl mx-auto text-center space-y-2">
          <div className="flex justify-center gap-0.5">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="text-[#FFB800] fill-[#FFB800]" size={14} />
            ))}
          </div>
          <p className="text-xs sm:text-sm text-[#1D1D1F] font-medium italic px-4">
            "Finalmente tutto in un posto solo!"
          </p>
          <p className="text-[10px] text-[#86868B]">Chi ci usa, ci ama</p>
        </div>
      </section>

      {/* Veterinari & Associazioni - Combined Compact */}
      <section id="veterinari" className="py-8 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Veterinari Card */}
            <div className="bg-gradient-to-br from-[#2E7D32] to-[#4CAF50] rounded-2xl p-5 sm:p-6 text-white relative overflow-hidden">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 rounded-full mb-3">
                <Stethoscope size={12} />
                <span className="text-[10px] sm:text-xs font-medium">Per Veterinari</span>
              </div>
              
              <h3 className="text-lg sm:text-xl font-bold mb-1.5">Portale B2B</h3>
              <p className="text-white/80 text-xs sm:text-sm mb-3">
                Gestisci pazienti, appuntamenti e certificazioni.
              </p>
              
              <div className="grid grid-cols-2 gap-1.5 mb-3">
                {[
                  { icon: Calendar, text: "Calendario" },
                  { icon: Users, text: "Pazienti" },
                  { icon: Shield, text: "Certificazioni" },
                  { icon: FileText, text: "Storico" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-white/90 text-[10px] sm:text-xs">
                    <item.icon size={12} />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => navigate('/vet/register')}
                className="h-9 px-4 bg-white text-[#2E7D32] hover:bg-white/90 rounded-full text-xs sm:text-sm font-semibold"
                data-testid="vet-cta"
              >
                Registrati
                <ArrowRight className="ml-1" size={14} />
              </Button>
              
              <div className="absolute right-0 bottom-0 w-24 h-24 bg-white/10 rounded-full blur-3xl" />
            </div>
            
            {/* Associazioni Card */}
            <div id="associazioni" className="bg-gradient-to-br from-[#E91E63] to-[#F48FB1] rounded-2xl p-5 sm:p-6 text-white relative overflow-hidden">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/20 rounded-full mb-3">
                <Heart size={12} />
                <span className="text-[10px] sm:text-xs font-medium">Associazioni e Canili</span>
              </div>
              
              <h3 className="text-lg sm:text-xl font-bold mb-1.5">Trova un amico</h3>
              <p className="text-white/80 text-xs sm:text-sm mb-3">
                Database ufficiale per animali in cerca di casa. Ogni adozione è una nuova vita.
              </p>
              
              <div className="flex items-center gap-2 mb-3">
                <div className="flex -space-x-2">
                  {[Dog, Cat, Rabbit].map((Icon, i) => (
                    <div key={i} className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center border-2 border-[#E91E63]">
                      <Icon className="text-white" size={12} />
                    </div>
                  ))}
                </div>
                <span className="text-xs text-white/80">Tanti amici ti aspettano</span>
              </div>
              
              <Button 
                onClick={() => navigate('/adozioni')}
                className="h-9 px-4 bg-white text-[#E91E63] hover:bg-white/90 rounded-full text-xs sm:text-sm font-semibold border-0"
                data-testid="association-cta"
              >
                Cerca un amico
                <ArrowRight className="ml-1" size={14} />
              </Button>
              
              <div className="absolute right-0 bottom-0 w-24 h-24 bg-white/10 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>
      {/* Final CTA - Compact */}
      <section className="py-8 sm:py-12 lg:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#FF6B4A] to-[#E55A3C]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-white tracking-tight mb-3">
            Inizia oggi, gratis
          </h2>
          <p className="text-white/80 text-xs sm:text-sm lg:text-base mb-4 lg:mb-6">Unisciti a chi ha già scelto il meglio per i propri animali</p>
          <Button 
            onClick={() => setShowAuth(true)}
            className="h-10 sm:h-11 lg:h-14 px-6 lg:px-10 bg-white text-[#FF6B4A] hover:bg-white/95 rounded-full text-sm lg:text-lg font-bold shadow-xl"
            data-testid="final-cta"
          >
            Crea il tuo account
          </Button>
        </div>
      </section>
      
      {/* App Mobile Section - Desktop only - BEFORE FOOTER */}
      <section className="hidden lg:block py-20 px-8 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-black/5 mb-6">
            <Smartphone className="text-[#FF6B4A]" size={18} />
            <span className="text-sm font-medium text-[#1D1D1F]">App Mobile</span>
          </div>
          
          <h2 className="text-4xl font-bold text-[#1D1D1F] mb-4">
            Zampa sempre con te
          </h2>
          <p className="text-lg text-[#86868B] mb-10 max-w-2xl mx-auto">
            Accedi ai dati dei tuoi animali da qualsiasi dispositivo. Le app iOS e Android stanno arrivando.
          </p>
          
          {/* Store badges */}
          <div className="flex justify-center items-center gap-6 mb-8">
            <div className="relative">
              <div className="bg-[#1D1D1F] text-white rounded-xl px-6 py-3 flex items-center gap-3 opacity-60">
                <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[10px] leading-none">Download on the</p>
                  <p className="text-lg font-semibold leading-tight">App Store</p>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 bg-[#FF6B4A] text-white text-[10px] font-bold px-2 py-1 rounded-full">Coming Soon</div>
            </div>
            <div className="relative">
              <div className="bg-[#1D1D1F] text-white rounded-xl px-6 py-3 flex items-center gap-3 opacity-60">
                <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                </svg>
                <div className="text-left">
                  <p className="text-[10px] leading-none">GET IT ON</p>
                  <p className="text-lg font-semibold leading-tight">Google Play</p>
                </div>
              </div>
              <div className="absolute -top-2 -right-2 bg-[#FF6B4A] text-white text-[10px] font-bold px-2 py-1 rounded-full">Coming Soon</div>
            </div>
          </div>
          
          <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-md border border-black/5">
            <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
              <Globe className="text-[#2E7D32]" size={18} />
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm text-[#1D1D1F]">Webapp disponibile ora</p>
              <p className="text-xs text-[#86868B]">Accedi da zampa.one su qualsiasi browser</p>
            </div>
            <CheckCircle className="text-[#2E7D32]" size={20} />
          </div>
        </div>
      </section>

      {/* Footer - Compact */}
      <footer className="py-6 px-4 sm:px-6 lg:px-8 bg-[#1D1D1F]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
                <PawPrint className="text-white" size={14} />
              </div>
              <span className="font-bold text-white text-sm">Zampa</span>
            </div>
            
            <div className="flex items-center gap-4 sm:gap-6 text-xs">
              <a href="mailto:hello@zampa.one" className="text-white/60 hover:text-white transition-colors flex items-center gap-1">
                <Mail size={12} /> hello@zampa.one
              </a>
              <a href="/privacy" className="text-white/60 hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="text-white/60 hover:text-white transition-colors">Termini</a>
            </div>
            
            <p className="text-white/40 text-[10px]">© 2026 Zampa</p>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={handleAuthClose}>
        <DialogContent className="max-w-[420px] rounded-3xl bg-white p-0 border-0 shadow-2xl">
          <div className="p-6 sm:p-8">
            <DialogHeader className="mb-5">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center mb-3 shadow-lg shadow-[#FF6B4A]/30">
                  <PawPrint className="text-white" size={28} />
                </div>
                <DialogTitle className="text-xl font-bold text-[#1D1D1F] text-center">
                  {userType ? 'Benvenuto in Zampa' : 'Chi sei?'}
                </DialogTitle>
                <p className="text-sm text-[#86868B] mt-1">
                  {userType ? 'Accedi o crea un account' : 'Seleziona il tuo profilo per continuare'}
                </p>
              </div>
            </DialogHeader>
            
            {/* Step 1: User Type Selection */}
            {!userType && (
              <div className="space-y-3">
                <button
                  onClick={() => setUserType('cliente')}
                  className="w-full p-4 rounded-2xl bg-[#F5F5F7] hover:bg-[#EBEBF0] transition-colors flex items-center gap-4 text-left group"
                  data-testid="user-type-cliente"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] flex items-center justify-center">
                    <Heart className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#1D1D1F]">Proprietario di animali</p>
                    <p className="text-sm text-[#86868B]">Gestisci il fascicolo dei tuoi pet</p>
                  </div>
                  <ArrowRight className="text-[#86868B] group-hover:text-[#FF6B4A] transition-colors" size={20} />
                </button>
                
                <button
                  onClick={() => navigate('/vet-auth')}
                  className="w-full p-4 rounded-2xl bg-[#F5F5F7] hover:bg-[#EBEBF0] transition-colors flex items-center gap-4 text-left group"
                  data-testid="user-type-vet"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2E7D32] to-[#4CAF50] flex items-center justify-center">
                    <Stethoscope className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#1D1D1F]">Veterinario / Clinica</p>
                    <p className="text-sm text-[#86868B]">Accedi al portale professionisti</p>
                  </div>
                  <ArrowRight className="text-[#86868B] group-hover:text-[#2E7D32] transition-colors" size={20} />
                </button>
                
                <button
                  onClick={() => navigate('/vet-auth?type=association')}
                  className="w-full p-4 rounded-2xl bg-[#F5F5F7] hover:bg-[#EBEBF0] transition-colors flex items-center gap-4 text-left group"
                  data-testid="user-type-canile"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E91E63] to-[#F48FB1] flex items-center justify-center">
                    <Building2 className="text-white" size={24} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#1D1D1F]">Associazione / Canile</p>
                    <p className="text-sm text-[#86868B]">Gestisci adozioni e animali</p>
                  </div>
                  <ArrowRight className="text-[#86868B] group-hover:text-[#E91E63] transition-colors" size={20} />
                </button>
              </div>
            )}
            
            {/* Step 2: Login/Register Form (only for clients) */}
            {userType === 'cliente' && (
              <>
                {/* Social Login */}
                <div className="flex justify-center gap-3 mb-5">
                  <Button
                    onClick={handleAppleLogin}
                    variant="outline"
                    className="w-12 h-12 rounded-xl border-[#E5E5E7] hover:bg-[#F5F5F7] p-0"
                    data-testid="apple-login-btn"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                  </Button>

                  <Button
                    onClick={handleGoogleLogin}
                    variant="outline"
                    className="w-12 h-12 rounded-xl border-[#E5E5E7] hover:bg-[#F5F5F7] p-0"
                    data-testid="google-login-btn"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </Button>
                </div>
                
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-[#E5E5E7]" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-4 text-sm text-[#86868B]">oppure con email</span>
                  </div>
                </div>
                
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-4 bg-[#F5F5F7] rounded-xl p-1 h-10">
                    <TabsTrigger value="login" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium text-sm">
                      Accedi
                    </TabsTrigger>
                    <TabsTrigger value="register" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium text-sm">
                      Registrati
                    </TabsTrigger>
                  </TabsList>
              
              <TabsContent value="login">
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1D1D1F] mb-1.5 block">Email</Label>
                    <Input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="nome@email.com"
                      className="h-12 rounded-xl bg-[#F5F5F7] border-0 focus:ring-2 focus:ring-[#FF6B4A]"
                      required
                      data-testid="login-email"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1D1D1F] mb-1.5 block">Password</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        placeholder="La tua password"
                        className="h-12 rounded-xl bg-[#F5F5F7] border-0 pr-12 focus:ring-2 focus:ring-[#FF6B4A]"
                        required
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[#86868B]"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-[#FF6B4A] hover:bg-[#E55A3C] text-white rounded-xl font-semibold"
                    data-testid="login-submit"
                  >
                    {isSubmitting ? "Accesso..." : "Accedi"}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium text-[#1D1D1F] mb-1.5 block">Nome</Label>
                    <Input
                      type="text"
                      value={registerName}
                      onChange={(e) => setRegisterName(e.target.value)}
                      placeholder="Il tuo nome"
                      className="h-12 rounded-xl bg-[#F5F5F7] border-0 focus:ring-2 focus:ring-[#FF6B4A]"
                      required
                      data-testid="register-name"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1D1D1F] mb-1.5 block">Email</Label>
                    <Input
                      type="email"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      placeholder="nome@email.com"
                      className="h-12 rounded-xl bg-[#F5F5F7] border-0 focus:ring-2 focus:ring-[#FF6B4A]"
                      required
                      data-testid="register-email"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#1D1D1F] mb-1.5 block">Password</Label>
                    <Input
                      type="password"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      placeholder="Crea una password"
                      className="h-12 rounded-xl bg-[#F5F5F7] border-0 focus:ring-2 focus:ring-[#FF6B4A]"
                      required
                      data-testid="register-password"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-12 bg-[#FF6B4A] hover:bg-[#E55A3C] text-white rounded-xl font-semibold"
                    data-testid="register-submit"
                  >
                    Crea account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Terms Dialog */}
      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle>Termini e Condizioni</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-[#86868B] mb-4">
              Continuando, accetti i nostri Termini di Servizio e la Privacy Policy.
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowTerms(false)} className="flex-1 rounded-xl">
                Annulla
              </Button>
              <Button onClick={confirmRegistration} disabled={isSubmitting} className="flex-1 bg-[#FF6B4A] hover:bg-[#E55A3C] rounded-xl">
                {isSubmitting ? "Creazione..." : "Accetto"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2FA Dialog */}
      <TwoFactorDialog
        isOpen={show2FA}
        onClose={() => setShow2FA(false)}
        onVerify={handle2FAVerify}
        mode={twoFAMode}
      />

      {/* Float animation styles */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 3.5s ease-in-out infinite 1s;
        }
      `}</style>
    </div>
  );
}
